import React, { useState, useEffect } from 'react';
import { Button, Input, Select, Card, Statistic, Spin, Empty } from 'antd';
import { PlusOutlined, ReloadOutlined, SearchOutlined, FileTextOutlined, CheckCircleOutlined, EditOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import * as blogPostApi from '../../api/blogPostApi';
import BlogPostForm from '../../components/admin/blog-post/BlogPostForm';
import BlogPostTable from '../../components/admin/blog-post/BlogPostTable';
import Modal from '../../components/common/Modal';

const { Option } = Select;

const BlogPostManagement = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBlogPost, setEditingBlogPost] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState({ total: 0, published: 0, drafts: 0 });

  useEffect(() => {
    fetchBlogPosts();
    fetchStats();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      setLoading(true);
      const response = await blogPostApi.getBlogPosts();

      if (response.blogPosts && Array.isArray(response.blogPosts)) {
        setBlogPosts(response.blogPosts);
      } else {
        setBlogPosts([]);
      }
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      toast.error('Failed to fetch blog posts');
      setBlogPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await blogPostApi.getBlogPostStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleCreate = () => {
    setEditingBlogPost(null);
    setShowForm(true);
  };

  const handleEdit = async (blogPost) => {
    try {
      // The list endpoint omits `content` for payload size — fetch the full
      // record (with content) before opening the edit form.
      const response = await blogPostApi.getBlogPost(blogPost._id);
      setEditingBlogPost(response.blogPost || blogPost);
      setShowForm(true);
    } catch (error) {
      console.error('Error loading blog post for edit:', error);
      toast.error('Failed to load blog post');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;

    try {
      await blogPostApi.deleteBlogPost(id);
      toast.success('Blog post deleted successfully');
      fetchBlogPosts();
      fetchStats();
    } catch (error) {
      console.error('Error deleting blog post:', error);
      const errorMsg = error?.message || error?.msg || 'Failed to delete blog post';
      toast.error(errorMsg);
    }
  };

  const handleToggle = async (id) => {
    try {
      await blogPostApi.toggleBlogPostStatus(id);
      toast.success('Blog post status updated');
      fetchBlogPosts();
      fetchStats();
    } catch (error) {
      console.error('Error toggling blog post status:', error);
      toast.error('Failed to update blog post status');
    }
  };

  const handleFormSubmit = () => {
    setShowForm(false);
    setEditingBlogPost(null);
    fetchBlogPosts();
    fetchStats();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingBlogPost(null);
  };

  const filteredBlogPosts = Array.isArray(blogPosts)
    ? blogPosts.filter(item => {
        const matchesSearch = item.title?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = statusFilter === 'all' ||
                             (statusFilter === 'published' && item.isPublished) ||
                             (statusFilter === 'draft' && !item.isPublished);
        return matchesSearch && matchesFilter;
      })
    : [];

  return (
    <>
      <div className="max-w-9xl mx-auto w-full" style={{ fontFamily: 'Rubik, sans-serif' }}>
        <div className="p-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'Rubik, sans-serif', color: '#470172' }}>
                Blog Posts
              </h1>
              <p className="text-gray-600" style={{ fontFamily: 'Rubik, sans-serif' }}>
                Write and publish blog posts shown on the homepage and the public blog page
              </p>
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
              size="large"
              style={{
                backgroundColor: '#470172',
                borderColor: '#470172',
                fontFamily: 'Rubik, sans-serif',
                fontSize: '16px',
                fontWeight: 400
              }}
            >
              Add Blog Post
            </Button>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card bordered={false} style={{
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              fontFamily: 'Rubik, sans-serif'
            }}>
              <Statistic
                title={<span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#000000' }}>Total Posts</span>}
                value={stats.total}
                prefix={<FileTextOutlined style={{ color: '#470172' }} />}
                valueStyle={{ fontFamily: 'Rubik, sans-serif', fontSize: '28px', fontWeight: 600, color: '#470172' }}
              />
            </Card>
            <Card bordered={false} style={{
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              fontFamily: 'Rubik, sans-serif'
            }}>
              <Statistic
                title={<span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#000000' }}>Published</span>}
                value={stats.published}
                prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                valueStyle={{ fontFamily: 'Rubik, sans-serif', fontSize: '28px', fontWeight: 600, color: '#52c41a' }}
              />
            </Card>
            <Card bordered={false} style={{
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              fontFamily: 'Rubik, sans-serif'
            }}>
              <Statistic
                title={<span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#000000' }}>Drafts</span>}
                value={stats.drafts}
                prefix={<EditOutlined style={{ color: '#faad14' }} />}
                valueStyle={{ fontFamily: 'Rubik, sans-serif', fontSize: '28px', fontWeight: 600, color: '#faad14' }}
              />
            </Card>
          </div>

          {/* Filters */}
          <Card bordered={false} style={{
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            marginBottom: '24px',
            fontFamily: 'Rubik, sans-serif'
          }}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                placeholder="Search by title..."
                prefix={<SearchOutlined style={{ color: '#470172' }} />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="large"
                style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400 }}
              />
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                size="large"
                style={{ width: '100%', fontFamily: 'Rubik, sans-serif' }}
              >
                <Option value="all">All Status</Option>
                <Option value="published">Published</Option>
                <Option value="draft">Draft</Option>
              </Select>
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchBlogPosts}
                size="large"
                style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400 }}
              >
                Refresh
              </Button>
            </div>
          </Card>

          {/* Blog Posts Table */}
          <Card bordered={false} style={{
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            fontFamily: 'Rubik, sans-serif'
          }}>
            {loading ? (
              <div className="text-center py-12">
                <Spin size="large" />
                <p style={{ marginTop: '16px', fontFamily: 'Rubik, sans-serif', color: '#666' }}>
                  Loading blog posts...
                </p>
              </div>
            ) : filteredBlogPosts.length === 0 ? (
              <Empty
                description={
                  <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', color: '#666' }}>
                    {blogPosts.length === 0
                      ? 'No blog posts yet. Click "Add Blog Post" to create one.'
                      : 'No blog posts match your filters.'}
                  </span>
                }
              />
            ) : (
              <BlogPostTable
                blogPosts={filteredBlogPosts}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onToggleStatus={handleToggle}
              />
            )}
          </Card>
        </div>
      </div>

      {/* Form Modal */}
      <Modal
        isOpen={showForm}
        onClose={handleFormCancel}
        title={editingBlogPost ? 'Edit Blog Post' : 'Add Blog Post'}
        width="900px"
      >
        <BlogPostForm
          blogPost={editingBlogPost}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
        />
      </Modal>
    </>
  );
};

export default BlogPostManagement;
